const { context } = require('../app-context');
const {
    isDeviceOffline,
    isOfflineError,
    isRecoverableServerError,
    isTransientNetworkError
} = require('utils/network');
const { createOfflineConfirmation } = require('utils/offline-confirmation');
const { createServiceUnavailableRecovery } = require('utils/service-unavailable-recovery');
const { createUpgradeGate, fetchWebVersion } = require('../utils/app-version-policy');

const internals = {};

internals.isSyncRequest = (config) => {

    if (config?.method?.toLowerCase() !== 'post' || typeof config.url !== 'string') {
        return false;
    }

    try {
        const { pathname } = new URL(config.url, 'https://nearpeer.invalid');
        return pathname === '/sync' || pathname === '/api/sync';
    }
    catch (err) {
        return false;
    }
};

module.exports = () => {

    const {
        redux: { hooks },
        http: { nearpeer },
        actions,
        selectors
    } = context;

    const {
        getIsOffline,
        getIsDowntimeMode,
        getDowntimeType
    } = selectors.all;

    const offline = (isOffline) => hooks.dispatch(actions.connectivity.offline({ isOffline }));
    const downtime = (isDowntime, message, type) => hooks.dispatch(actions.app.setIsDowntimeMode({ value: isDowntime, message, type }));
    const forceAppUpgrade = (shouldForceAppUpgrade, apiAppVersion, frontendAppVersion) => {

        if (!shouldForceAppUpgrade && !selectors.all.getIsForceAppUpgradeMode(hooks.getState())) {
            return;
        }

        hooks.dispatch(actions.app.setIsForceAppUpgradeMode({ value: shouldForceAppUpgrade, apiAppVersion, frontendAppVersion }));
    };

    internals.recovery = createServiceUnavailableRecovery({
        http: nearpeer,
        onAvailable: () => downtime(false),
        onUnavailable: () => downtime(
            true,
            'Nearpeer is temporarily unavailable. You can try this request again or continue without it.',
            'service-unavailable'
        )
    });

    internals.offlineConfirmation = createOfflineConfirmation({
        http: nearpeer,
        isDeviceOffline,
        onOffline: () => offline(true),
        onOnline: () => {

            if (getIsOffline(hooks.getState())) {
                offline(false);
            }
        }
    });

    const handleOffline = () => internals.offlineConfirmation.suspect();
    const handleOnline = () => internals.offlineConfirmation.online();
    const syncDeviceConnection = () => {

        if (isDeviceOffline()) {
            internals.offlineConfirmation.suspect();
        }
        else {
            internals.offlineConfirmation.online();
        }
    };

    if (typeof window !== 'undefined') {
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
    }

    if (typeof document !== 'undefined') {
        document.addEventListener('offline', handleOffline);
        document.addEventListener('online', handleOnline);
        document.addEventListener('deviceready', syncDeviceConnection);
    }

    let storage;

    try {
        storage = typeof window !== 'undefined' ? window.sessionStorage : undefined;
    }
    catch (err) {
        // Private browsing can disable session storage.
    }

    internals.upgrade = createUpgradeGate({
        installedVersion: process.env.APP_VERSION,
        isNative: typeof window !== 'undefined' && Boolean(window.cordova),
        storage,
        onChange: forceAppUpgrade,
        readWebVersion: () => fetchWebVersion((...args) => window.fetch(...args))
    });

    nearpeer.interceptors.response.use(
        (response) => {

            if (internals.isSyncRequest(response.config)) {
                internals.upgrade.update(response.headers?.['x-min-app-version']);
            }

            const state = hooks.getState();

            internals.offlineConfirmation.clear();

            if (getIsOffline(state)) {
                offline(false);
            }
            else if (getIsDowntimeMode(state) && getDowntimeType(state) !== 'service-unavailable') {
                downtime(false);
                // We used to reload but it just causes a white screen
                // window.location.reload();
            }

            return response;
        },
        (error) => {

            if (internals.isSyncRequest(error?.config || error?.response?.config)) {
                // Failed sync cannot confirm a policy. Keep the app usable until the next sync.
                internals.upgrade.update(undefined);
            }

            const is503 = error?.response?.status === 503;
            const isDowntimeStatus = error?.response?.data?.status === 'downtime';

            if (is503 && isDowntimeStatus && !internals.recovery.hasPendingRequest()) {
                downtime(true, error?.response?.data?.message, 'maintenance');

                if (error?.config?.url === '/ping') {
                    hooks.dispatch(actions.alerts.notification.push({
                        message: 'Nearpeer is temporarily down for maintenance.'
                    }));
                }
            }
            else if (isRecoverableServerError(error) && error?.config?.serviceUnavailableRetry) {
                return internals.recovery.handleResponseError(error);
            }
            else if (isTransientNetworkError(error)) {
                internals.offlineConfirmation.suspect();

                if (!isDeviceOffline() && error?.config?.serviceUnavailableRetry) {
                    return internals.recovery.handleTransientError(error);
                }
            }
            else if (isOfflineError(error)) {
                internals.offlineConfirmation.suspect();
            }

            return Promise.reject(error);
        }
    );

    return () => {

        if (typeof window !== 'undefined') {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        }

        if (typeof document !== 'undefined') {
            document.removeEventListener('offline', handleOffline);
            document.removeEventListener('online', handleOnline);
            document.removeEventListener('deviceready', syncDeviceConnection);
        }

        internals.offlineConfirmation.dispose();
    };
};

module.exports.retryPendingRequest = () => internals.recovery?.retryPendingRequest();

module.exports.continueWithoutRetry = () => internals.recovery?.continueWithoutRetry();

module.exports.continueAppUpgrade = () => internals.upgrade?.dismiss('deferred');

module.exports.attemptAppUpgrade = () => internals.upgrade?.dismiss('attempted');
